import * as yup from "yup";
import "yup-phone";
import { isValidPhoneNumber, parsePhoneNumber, validatePhoneNumberLength } from "libphonenumber-js";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

yup.addMethod(yup.string, "validPhone", function (message) {
  return this.test("validPhone", message, function (value) {
    const { path, createError } = this;

    if (!value) return true; // Let required() handle empty values

    // react-phone-input-2 might return numbers without '+', so we ensure international format for validation
    const formattedValue = value.startsWith("+") ? value : `+${value}`;

    // 1. Strict check first (covers valid India, valid HK, valid US, etc.)
    if (isValidPhoneNumber(formattedValue)) {
      return true;
    }

    // 2. Custom Logic & Error Messages
    try {
      const phoneNumber = parsePhoneNumber(formattedValue);
      const country = phoneNumber.country;
      const nationalNumber = phoneNumber.nationalNumber;

      // HONG KONG Custom Rules
      if (country === "HK") {
        // Allow test/unassigned numbers if they are strictly 8 digits and sane format
        if (nationalNumber.length === 8 && /^[2-9]/.test(nationalNumber)) {
          return true;
        }
        if (nationalNumber.length !== 8) {
          return createError({ path, message: "Phone number must be 8 digits" });
        }
      }

      // 3. Generic Length Check for ALL countries
      // If we are here, strict validation failed. Let's see WHY.
      const lengthParams = validatePhoneNumberLength(formattedValue);
      if (lengthParams === "TOO_SHORT") {
        return createError({ path, message: "Phone number is too short" });
      }
      if (lengthParams === "TOO_LONG") {
        return createError({ path, message: "Phone number is too long" });
      }

      // If length is technically "valid" but number is invalid (e.g. invalid pattern),
      // we fall through to generic error.
      // But we can also be helpful for specific known lengths if needed.
      if (country === "IN" && nationalNumber.length !== 10) {
        return createError({ path, message: "Phone number must be 10 digits" });
      }

    } catch (e) {
      // Parsing logic failed, so it's definitely invalid
    }

    return createError({ path, message: message || "Invalid phone number" });
  });
});

yup.addMethod(yup.string, "validEmail", function (message) {
  return this.test("validEmail", message, function (value) {
    const { path, createError } = this;
    if (!value) return true;
    const trimmedValue = value.trim();
    // Improved regex to handle double dots in username AND domain, and whitelisted TLDs
    const emailRegex = /^[a-zA-Z0-9]+(?:[._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.(com|in|org|net|edu|gov|co|info|biz|me|uk|us|ca|au)$/i;
    if (!emailRegex.test(trimmedValue)) {
      return createError({ path, message: message || "Please enter a valid email address" });
    }
    return true;
  });
});

export const parentFormAnimated = yup.object().shape({
  childName: yup.string().required("This field is required"),
  dob: yup.date().required("This field is required"),
  gender: yup.string().required("Select any one option"),

  address: yup.string().required("This field is required"),
  alternativephoneNumber: yup
    .string()
    .validPhone("Alternative phone number is not valid")
    .notOneOf([yup.ref('phoneNumber')], 'Alternative phone number cannot be the same as phone number'),

  phoneNumber: yup
    .string()
    .validPhone("Phone number is not valid")
    .required("This field is required"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),

  schoolDropdown: yup.string().required("Select an option"),

  // Conditionally required schoolName field
  schoolName: yup.string().when("schoolDropdown", {
    is: "Other",
    then: (schema) => schema.required("This field is required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  howdoyouknow: yup.string().required("Select an option"),
  expectation: yup.string().required("This field is required"),

  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be less than 20 characters")
    .matches(/[a-zA-Z]/, "Password must contain both letters and numbers")
    .matches(/\d/, "Password must contain a number")
    .required("Password is required"),

  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),

  captcha: yup.string().required("Captcha is required"),
  agree: yup.string().required("This field is required"),
});

export const gettingStartedForm = yup.object().shape({
  childName: yup.string().required("This field is required"),
  dob: yup.date().required("This field is required"),
  gender: yup.string().required("Select any one option"),
  reason: yup.string().required("Select an option"),
  parentName: yup.string().required("This field is required"),
  phoneNumber: yup
    .string()
    .validPhone("Phone number is not valid")
    .required("This field is required"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  howdoyouknow: yup.string().required("Select an option"),
  relation: yup.string().required("Select an option"),
  captcha: yup.string().required("Captcha is required"),
});

export const parentPageFormstep1 = yup.object().shape({
  schoolName: yup.string().required("This field is required"),
  principalName: yup.string().required("This field is required"),
  buildingName: yup.string().required("This field is required"),
  buildingAddress: yup.string().required("This field is required"),
  buildingState: yup.string().required("This field is required"),
  yearEstablishment: yup
    .number()
    .integer()
    .max(9999, "Year of establishment must be 4 digits")
    .required("This field is required"),
  buildingContact: yup
    .string()
    .required("Building contact is required"),
  administrationContact: yup
    .string()
    .required("Administration contact is required"),
  // phoneNumber: yup
  //   .string()
  //   .matches(phoneRegExp, "Phone number is not valid")
  //   .min(10, "Must be exactly 10 digits")
  //   .max(10, "Must be exactly 10 digits")
  //   .required("This field is required"),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .validPhone("Invalid phone number"),
  telephoneNumber: yup
    .string()
    .required("Telephone number is required")
    .validPhone("Invalid telephone number"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  studentPopulation: yup.number().integer().required("This field is required"),
  teacherPopulation: yup.number().integer().required("This field is required"),
  schoolType: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  studentTeacherRatio: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
});
export const parentPageFormstep2 = yup.object().shape({
  infrastructureFacility: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  curriculamSupport: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  schoolPolicy: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
});

export const parentPageFormstep3 = yup.object().shape({
  multidisciplinaryTeam: yup.string().required("This field is required"),
  inclusionPolicy: yup.string().required("This field is required"),
  // multidisciplinaryTeamDes: yup.string().required("This field is required"),
  multidisciplinaryTeamDes: yup
    .string()
    .when("multidisciplinaryTeam", {
      is: "Yes",
      then: (schema) => schema.required("Description is required for multidisciplinary team approach"),
      otherwise: (schema) => schema.notRequired(),
    }),
  captcha: yup.string().required("Captcha is required"),
});

export const serviceProviderPageForm = yup.object().shape({
  serviceName: yup.string().required("This field is required"),
  universityName: yup.string().required("This field is required"),
  gender: yup.string().required("Select any one option"),
  professionalCharges: yup.string().required("This field is required"),
  yearOfCompletion: yup.date().required("This field is required"),
  specialistIn: yup.string().required("This field is required"),
  workExperience: yup.string().required("This field is required"),
  // phoneNumber: yup
  //   .string()
  //   .required("This field is requiredegegegege")
  //   .min(10, "Must be exactly 10 digits")
  //   .max(10, "Must be exactly 10 digits")
  //   .matches(phoneRegExp, "Phone number is not valid"),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .validPhone("Invalid phone number"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  agree: yup.bool().required("This field is required"),
  captcha: yup.string().required("Captcha is required"),
  specialization: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  modeOfOperation: yup.string().required("Please select any option"),
  modeOfDelivery: yup.string().required("Please select any option"),
  modeOfService: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  professionalQualification: yup.string().required("Please select any option"),
});

export const serviceProviderPageForm2 = yup.object().shape({
  serviceName: yup.string().required("This field is required"),
  universityName: yup.string().required("This field is required"),
  gender: yup.string().required("Select any one option"),
  professionalCharges: yup.string().required("This field is required"),
  yearOfCompletion: yup.date().required("This field is required"),
  specialistIn: yup.string().required("This field is required"),
  workExperience: yup.string().required("This field is required"),
  phoneNumber: yup
    .string()
    .required("This field is required")
    .validPhone("Phone number is not valid"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  agree: yup.bool().required("This field is required"),
  captcha: yup.string().required("Captcha is required"),
  specialization: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  modeOfOperation: yup.string().required("Please select any option"),
  modeOfDelivery: yup.string().required("Please select any option"),
  modeOfService: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  professionalQualification: yup.string().required("Please select any option"),
  organisationName: yup.string().required("This field is required"),
  organisationHeadName: yup.string().required("This field is required"),
  organisationEmail: yup
    .string()
    .validEmail()
    .required("This field is required"),
  organisationWebsite: yup.string().required("This field is required"),
  anySpecifications: yup.string().required("This field is required"),
});

export const organisationPageForm = yup.object().shape({
  serviceName: yup.string().required("This field is required"),
  universityName: yup.string().required("This field is required"),
  gender: yup.string().required("Select any one option"),
  professionalCharges: yup.string().required("This field is required"),
  yearOfCompletion: yup.date().required("This field is required"),
  specialistIn: yup.string().required("This field is required"),
  workExperience: yup.string().required("This field is required"),
  phoneNumber: yup
    .string()
    .required("This field is required")
    .validPhone("Phone number is not valid"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  agree: yup.bool().required("This field is required"),
  captcha: yup.string().required("Captcha is required"),
  specialization: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  modeOfOperation: yup.string().required("Please select any option"),
  modeOfDelivery: yup.string().required("Please select any option"),
  modeOfService: yup
    .array()
    .min(1, "This field is required")
    .of(yup.string())
    .required("This field is required"),
  professionalQualification: yup.string().required("Please select any option"),
  organisationName: yup.string().required("This field is required"),
  organisationHeadName: yup.string().required("This field is required"),
  organisationEmail: yup
    .string()
    .validEmail()
    .required("This field is required"),
  organisationWebsite: yup.string().required("This field is required"),
  anySpecifications: yup.string().required("This field is required"),
});

export const internPageForm = yup.object().shape({
  internName: yup.string().required("This field is required"),
  // phoneNumber: yup
  //   .string()
  //   .matches(phoneRegExp, "Phone number is not valid")
  //   .min(10, "Must be exactly 10 digits")
  //   .max(10, "Must be exactly 10 digits")
  //   .required("This field is required"),
  //   parentName: yup.string().required("This field is required"),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .validPhone("Invalid phone number"),

  parentName: yup
    .string()
    .required("Parent/Guardian phone number is required")
    .validPhone("Invalid phone number")
    .notOneOf([yup.ref('phoneNumber')], 'Parent/Guardian phone number cannot be the same as phone number'),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  dob: yup.date().required("This field is required"),
  internDob: yup.string().required("This field is required"),
  workinghour: yup.string().required("This field is required"),
  agree: yup
    .bool()
    .oneOf([true], "You need to accept the terms and conditions")
    .required("You need to accept the terms and conditions"),
  captcha: yup.string().required("Captcha is required"),
  // shortAboutELiNA: yup.string().required("This field is required"),
  // knowAboutELiNA: yup.string().required("This field is required"),
  // lookingAboutELiNA: yup.string().required("This field is required"),
  shortAboutELiNA: yup
    .mixed()
    .test("fileSize", "File too large (max 2MB)", (value) => {
      return value && value.size <= 2 * 1024 * 1024;
    })
    .test(
      "fileFormat",
      "Unsupported file format. Only PDF and DOCX allowed",
      (value) => {
        return (
          value &&
          [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ].includes(value.type)
        );
      }
    )
    .required("This field is required"),

  // Validate file types and size for the knowAboutELiNA field
  knowAboutELiNA: yup
    .mixed()
    .test("fileSize", "File too large (max 2MB)", (value) => {
      return value && value.size <= 2 * 1024 * 1024;
    })
    .test(
      "fileFormat",
      "Unsupported file format. Only PDF and DOCX allowed",
      (value) => {
        return (
          value &&
          [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ].includes(value.type)
        );
      }
    )
    .required("This field is required"),

  // Validate file types and size for the lookingAboutELiNA field
  lookingAboutELiNA: yup
    .mixed()
    .test("fileSize", "File too large (max 2MB)", (value) => {
      return value && value.size <= 2 * 1024 * 1024;
    })
    .test(
      "fileFormat",
      "Unsupported file format. Only PDF and DOCX allowed",
      (value) => {
        return (
          value &&
          [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ].includes(value.type)
        );
      }
    )
    .required("This field is required"),
});

export const comments = yup.object().shape({
  name: yup.string().required("This field is required"),
  phonenumber: yup
    .string()
    .validPhone("Phone number is not valid")
    .required("This field is required"),
  email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  comment: yup.string().required("This field is required"),

  subject: yup.string().required("This field is required"),

  // captcha: yup.string().required("Captcha is required"),
});

export const contactUsForm = yup.object().shape({
  floating_first_name: yup.string().required("This field is required"),
  floating_last_name: yup.string().required("This field is required"),
  floating_email: yup
    .string()
    .validEmail()
    .required("This field is required"),
  // floating_phone: yup
  //   .string()
  //   .matches(phoneRegExp, "Phone number is not valid")
  //   .min(10, "Must be exactly 10 digits")
  //   .max(10, "Must be exactly 10 digits")
  //   .required("This field is required"),
  floating_phone: yup
    .string()
    .required("Phone number is required")
    .validPhone("Invalid phone number"),

  message: yup.string().required("This field is required"),
  captcha: yup.string().required("This field is required"),
});
